import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Every URL our site has, for the review card's new-page guard: a rival page
 * whose final path segment we already have is not a page to write
 * (`newPageIdeaBlocked` in shared/src/review/review-supporting-pages.ts). The
 * server-side replay reads the same list (`review-page-sources.ts`).
 *
 * Cached per domain, as `TechnicalRollupService` is: every page on the belt
 * needs the same list, and it only moves when a crawl adds pages.
 */
@Injectable({ providedIn: 'root' })
export class OwnSiteUrlsService {
  private readonly API_URL = environment.apiUrl + '/v1';
  private readonly cache = new Map<string, Observable<string[] | null>>();

  constructor(private http: HttpClient) {}

  /**
   * The site's URLs, or null when the read failed. A failure is not cached:
   * the next page to ask gets a real try.
   */
  getUrls(domain: string): Observable<string[] | null> {
    const key = (domain || '').trim().toLowerCase();
    const cached = this.cache.get(key);
    if (cached) return cached;

    const request = this.http
      .get<{ success: boolean; data: { domain: string | null; urls: string[] } | null }>(
        `${this.API_URL}/urls/own-site-urls`,
        { params: { domain: key } }
      )
      .pipe(
        map((res) => (Array.isArray(res?.data?.urls) ? res.data!.urls : null)),
        catchError(() => of(null)),
        tap((urls) => {
          if (!urls) this.cache.delete(key);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.cache.set(key, request);
    return request;
  }
}
